'use client';

import { format, getDaysInMonth, isValid, parse } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';

import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DatePickerInputProps {
  value: Date | null | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: (date: Date) => boolean;
  startMonth?: Date;
  endMonth?: Date;
  id?: string;
}

// Leap year used for day-in-month validation when year is incomplete
// 2000 is a leap year, allowing Feb 29 while user is still typing
const REFERENCE_LEAP_YEAR = 2000;

// ISO date pattern: yyyy-mm-dd
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatDateForDisplay(date: Date | null | undefined): string {
  if (!date) {
    return '';
  }
  return format(date, 'dd/MM/yyyy');
}

// Format a string of digits into dd/mm/yyyy format
export function formatWithSlashes(digits: string): string {
  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
}

// Extract just the digits from a formatted string, handling ISO date paste
export function extractDigits(input: string): string {
  // Check if input is ISO format (yyyy-mm-dd) and convert to ddmmyyyy
  const isoMatch = input.match(ISO_DATE_PATTERN);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}${month}${year}`;
  }
  return input.replace(/\D/g, '');
}

// Validate date components and return error message if invalid
export function validateDateInput(digits: string, minYear: number, maxYear: number): string | null {
  if (digits.length < 2) return null; // Still typing day

  const day = parseInt(digits.slice(0, 2), 10);
  if (day < 1 || day > 31) {
    return 'Day must be between 01 and 31';
  }

  if (digits.length < 4) return null; // Still typing month

  const month = parseInt(digits.slice(2, 4), 10);
  if (month < 1 || month > 12) {
    return 'Month must be between 01 and 12';
  }

  // Validate day against month (e.g., no 31st of February)
  if (digits.length >= 4) {
    // Use reference leap year when year isn't complete, actual year when complete
    const referenceYear =
      digits.length >= 8 ? parseInt(digits.slice(4, 8), 10) : REFERENCE_LEAP_YEAR;
    if (referenceYear >= minYear && referenceYear <= maxYear) {
      const daysInMonth = getDaysInMonth(new Date(referenceYear, month - 1));
      if (day > daysInMonth) {
        return `Day must be between 01 and ${daysInMonth} for this month`;
      }
    }
  }

  if (digits.length < 8) return null; // Still typing year

  const year = parseInt(digits.slice(4, 8), 10);
  if (year < minYear) {
    return `Year must be ${minYear} or later`;
  }
  if (year > maxYear) {
    return `Year must be ${maxYear} or earlier`;
  }

  return null;
}

// Try to parse a complete date string (dd/mm/yyyy) - returns undefined if incomplete or invalid
export function parseCompleteDate(input: string): Date | undefined {
  const digits = extractDigits(input);
  if (digits.length !== 8) return undefined;

  const formatted = formatWithSlashes(digits);
  const parsedDate = parse(formatted, 'dd/MM/yyyy', new Date());

  if (isValid(parsedDate)) {
    // Verify the parsed date matches input (catches invalid dates like Feb 30)
    const day = parseInt(digits.slice(0, 2), 10);
    const month = parseInt(digits.slice(2, 4), 10);
    if (parsedDate.getDate() !== day || parsedDate.getMonth() + 1 !== month) {
      return undefined;
    }

    // Set to noon UTC to avoid timezone day-shift issues
    return new Date(
      Date.UTC(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate(), 12, 0, 0),
    );
  }

  return undefined;
}

export function DatePickerInput({
  value,
  onChange,
  placeholder = 'dd/mm/yyyy',
  disabled,
  startMonth = new Date(1900, 0),
  endMonth = new Date(),
  id,
}: DatePickerInputProps) {
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState<Date | undefined>(value ?? undefined);
  const [inputValue, setInputValue] = React.useState(formatDateForDisplay(value));
  const [isFocused, setIsFocused] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const minYear = startMonth.getFullYear();
  const maxYear = endMonth.getFullYear();

  // Update input value when external value changes, but only when not focused
  // This prevents overwriting user input while they're typing
  React.useEffect(() => {
    if (!isFocused) {
      setInputValue(formatDateForDisplay(value));
      setError(null);
    }
    if (value) {
      setMonth(value);
    }
  }, [value, isFocused]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawInput = e.target.value;

    // Extract only digits from input
    const digits = extractDigits(rawInput);

    // Limit to 8 digits (ddmmyyyy)
    const limitedDigits = digits.slice(0, 8);

    // Format with slashes
    const formatted = formatWithSlashes(limitedDigits);
    setInputValue(formatted);

    // Validate the input
    let validationError = validateDateInput(limitedDigits, minYear, maxYear);

    // Only update form value when:
    // 1. Field is explicitly cleared → emit undefined
    // 2. Complete valid date is entered → emit the date
    // Don't emit for incomplete/invalid input to preserve existing value
    if (limitedDigits.length === 0) {
      onChange(undefined);
      setError(null);
    } else if (limitedDigits.length === 8 && !validationError) {
      const parsedDate = parseCompleteDate(formatted);
      if (parsedDate) {
        onChange(parsedDate);
        setMonth(parsedDate);
        setError(null);
      } else {
        // Parse failed (e.g., Feb 29 on non-leap year) - show error
        const month = parseInt(limitedDigits.slice(2, 4), 10);
        const year = parseInt(limitedDigits.slice(4, 8), 10);
        const daysInMonth = getDaysInMonth(new Date(year, month - 1));
        validationError = `Day must be between 01 and ${daysInMonth} for this month`;
        setError(validationError);
      }
    } else {
      setError(validationError);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      // Set to noon UTC to avoid timezone day-shift issues
      const noonUtc = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0),
      );
      onChange(noonUtc);
      setInputValue(formatDateForDisplay(noonUtc));
    } else {
      onChange(undefined);
      setInputValue('');
    }
    setOpen(false);
  };

  const errorId = id ? `${id}-error` : 'date-picker-error';

  return (
    <div className="space-y-1">
      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="relative flex gap-2">
        <Input
          id={id}
          value={inputValue}
          placeholder={placeholder}
          className={cn('bg-background pr-10', error && 'border-destructive')}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          inputMode="numeric"
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md hover:bg-accent"
            >
              <CalendarIcon className="size-4 text-muted-foreground" />
              <span className="sr-only">Select date</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="end" sideOffset={10}>
            <Calendar
              mode="single"
              selected={value ?? undefined}
              captionLayout="dropdown"
              month={month}
              onMonthChange={setMonth}
              onSelect={handleSelect}
              disabled={disabled}
              startMonth={startMonth}
              endMonth={endMonth}
              autoFocus
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
